import React, { memo, useContext, useCallback, useState, useRef, useEffect } from 'react';
import type { NodeProps } from 'reactflow';
import { GraphViewContext } from '../GraphView';
import { BaseNode } from './BaseNode';
import type { CountCondition } from '../../../../types/policy';

/**
 * Type-safe helper to get operator value from count condition
 */
function getCountConditionValue(condition: CountCondition | null | undefined, operator: string): unknown {
  if (!condition || typeof condition !== 'object') return '';
  if (operator in condition) {
    return (condition as unknown as Record<string, unknown>)[operator];
  }
  return '';
}

interface ActionButtonProps {
  onClick: React.MouseEventHandler<HTMLButtonElement>;
  label: string;
  tooltip: string;
  variant?: 'default' | 'danger';
}
const ActionButton: React.FC<ActionButtonProps> = ({ onClick, label, tooltip, variant = 'default' }) => (
  <button
    onClick={onClick}
    title={tooltip}
    className={
      `p-2 text-xs rounded transition-colors duration-150
      ${variant === 'danger'
        ? 'text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900'
        : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'}`
    }
  >
    {label}
  </button>
);

export const CountNode: React.FC<NodeProps> = memo(({ data, selected }) => {
  const handlers = useContext(GraphViewContext);
  if (!handlers) {
    console.error('[CountNode] Missing GraphViewContext');
    return null;
  }

  // Extract collapse handlers and state
  const { handleToggleCollapse, collapsedPaths } = handlers;

  const path = data.path ?? [];

  // Determine collapsed state for this subtree
  const serialized = JSON.stringify(path);
  const isCollapsed = collapsedPaths.includes(serialized);
  const toggleCollapse = useCallback(() => handleToggleCollapse(path), [handleToggleCollapse, path]);

  // Extract count condition data for display
  const cc: CountCondition | null = data.countData || null;
  const isValueCount = cc?.count ? 'value' in cc.count : false;
  const fieldLabel = isValueCount ? 'Value' : 'Field';
  const fieldDisplay = String(isValueCount ? cc?.count?.value : cc?.count?.field ?? '').split(' (')[0];
  const operator = Object.keys(cc ?? {}).find((k) => k !== 'count') ?? 'equals';
  const rawVal = getCountConditionValue(cc, operator);
  const valStr = Array.isArray(rawVal) ? `[${rawVal.join(', ')}]` : String(rawVal);

  const handleEdit = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      handlers.onEditNode(path);
    },
    [handlers, path]
  );

  const handleDelete = useCallback(() => handlers.handleDeleteNode(path), [handlers, path]);
  const addCondition = useCallback(
    () => handlers.handleAddCondition([...path, 'count', 'where']),
    [handlers, path]
  );
  const addAllOf = useCallback(
    () => handlers.handleAddAllOfGroup([...path, 'count', 'where']),
    [handlers, path]
  );
  const addCount = useCallback(
    () => handlers.handleAddCountCondition([...path, 'count', 'where']),
    [handlers, path]
  );
  const toggleNot = useCallback(() => handlers.handleToggleNot(path), [handlers, path]);

  // State for overflow menu of hidden actions
  const [menuOpen, setMenuOpen] = useState(false);
  const toggleMenu = useCallback((e: React.MouseEvent) => { e.stopPropagation(); setMenuOpen(o => !o); }, []);
  // Ref and effect for click-away to close menu
  const wrapperRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuOpen && wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside, true);
    return () => document.removeEventListener('mousedown', handleClickOutside, true);
  }, [menuOpen]);

  return (
    <div ref={wrapperRef} className="w-full h-full">
      <BaseNode
        selected={selected}
        bgClasses="from-purple-50 to-purple-100 dark:from-purple-800 dark:to-purple-900"
        ringColor="ring-indigo-500"
        header="Count"
        footer={null}
      >
        {/* Collapse button at top-left */}
        <div className="absolute top-2 left-2">
          <ActionButton
            onClick={toggleCollapse}
            label={isCollapsed ? '➕' : '➖'}
            tooltip={isCollapsed ? 'Expand subtree' : 'Collapse subtree'}
          />
        </div>
        {/* Menu opener at top-right */}
        <div className="absolute top-2 right-2">
          <ActionButton onClick={toggleMenu} label="⋮" tooltip="More actions" />
        </div>
        {/* Popover menu of hidden actions */}
        {menuOpen && (
          <div className="absolute top-8 right-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded shadow-md p-2 flex flex-col space-y-1 z-20">
            <ActionButton onClick={addCondition} label="📝" tooltip="Add Condition" />
            <ActionButton onClick={addAllOf} label="📂" tooltip="Group Condition" />
            <ActionButton onClick={addCount} label="🔢" tooltip="Add Count" />
            <ActionButton onClick={toggleNot} label="🚫" tooltip="Toggle NOT" />
            <ActionButton onClick={handleEdit} label="✏️" tooltip="Edit" />
            <ActionButton onClick={handleDelete} label="🗑️" tooltip="Delete" variant="danger" />
          </div>
        )}
        {/* Main content */}
        <div
          title={`${fieldLabel}: ${fieldDisplay} ${operator} ${valStr}`}
          className="mt-1 w-full text-xs text-gray-700 dark:text-gray-200 space-y-1"
        >
          <div className="truncate text-teal-700 dark:text-teal-300">
            <span className="font-semibold text-gray-500 dark:text-gray-400 mr-1">{fieldLabel}:</span>
            {fieldDisplay}
          </div>
          <div className="truncate">{operator}</div>
          <div className="truncate text-green-700 dark:text-green-300">{valStr}</div>
        </div>
      </BaseNode>
    </div>
  );
});

CountNode.displayName = 'CountNode'; 